// Lightweight, dependency-free password strength + a minimum-strength gate.
// Mirrors backend/src/lib/passwordStrength.js — keep the two in sync.
//
// Design note: we deliberately do NOT mandate specific character classes
// (the "must contain 1 upper, 1 digit, 1 special" rule). That's outdated
// guidance (NIST SP 800-63B) that produces predictable passwords like
// "Password1!". Instead we score on length + variety and block only
// genuinely weak / known-common passwords.

export const REQUIREMENTS = [
  { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "number", label: "A number", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "A symbol (!@#$…)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

// A small blocklist of the most-abused passwords — the worst offenders that
// would otherwise sneak past a naive length/variety check. Not exhaustive.
const COMMON_PASSWORDS = new Set([
  "password", "password1", "password123", "passw0rd", "12345678", "123456789",
  "1234567890", "11111111", "00000000", "qwerty123", "qwertyuiop", "iloveyou",
  "welcome1", "welcome123", "admin123", "letmein1", "abc12345", "123123123",
  "1q2w3e4r", "1qaz2wsx", "asdfghjkl", "zxcvbnm1", "football1", "sunshine1",
]);

export function checkRequirements(password = "") {
  return REQUIREMENTS.map((r) => ({ key: r.key, label: r.label, met: r.test(password) }));
}

export function isCommonPassword(password = "") {
  return COMMON_PASSWORDS.has(String(password).toLowerCase());
}

// 0..4 strength. Rewards length (so passphrases score well) and variety.
export function scorePassword(password = "") {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  const variety =
    (/[a-z]/.test(password) ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/[0-9]/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
  if (variety >= 2) score += 1;
  if (variety >= 3) score += 1;
  if (isCommonPassword(password)) score = Math.min(score, 1);
  return Math.min(4, score);
}

export const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Good", "Strong"];

export function getStrengthLabel(password = "") {
  return STRENGTH_LABELS[scorePassword(password)] || "Very weak";
}

export function getStrengthColor(password = "") {
  return (
    ["#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e"][scorePassword(password)] ||
    "#ef4444"
  );
}

// ── The gate ──────────────────────────────────────────────────────────────
// The minimum we'll accept. Blocks trivially-weak + known-common passwords
// without the composition-rule theatre.
export const MIN_ACCEPTABLE_SCORE = 2; // "Fair"

export function meetsMinStrength(password = "") {
  if (!password || password.length < 8) return false;
  if (isCommonPassword(password)) return false;
  return scorePassword(password) >= MIN_ACCEPTABLE_SCORE;
}

export function passwordGateError(password = "") {
  if (!password || password.length < 8) return "Password must be at least 8 characters.";
  if (isCommonPassword(password))
    return "That password is too common — choose something less guessable.";
  if (scorePassword(password) < MIN_ACCEPTABLE_SCORE)
    return "Password is too weak — make it longer or mix in letters, numbers, or symbols.";
  return "";
}
